import { useCallback, useEffect, useState } from "react";
import {
    SCORE_EVENT,
    addSessionSeconds,
    getSessionScore,
    resetSessionScore,
} from "../../helpers/sessionScore";
import { recordAward, type TrophyFlow } from "../trophies";
import { currentScanIsRepeat } from "../scanGuard";

export interface SessionScoreApi {
    total: number;
    /**
     * Bank the seconds saved. Passing the flow feeds the trophies tracker
     * (rolling-window records, golden hour) — every scoring screen should
     * name its flow; omitting it only banks the points.
     */
    addSeconds: (n: number, flow?: TrophyFlow) => void;
    reset: () => void;
}

export const useSessionScore = (): SessionScoreApi => {
    const [total, setTotal] = useState<number>(() => getSessionScore());

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent<number>).detail;
            setTotal(typeof detail === "number" ? detail : getSessionScore());
        };
        window.addEventListener(SCORE_EVENT, handler as EventListener);
        return () => window.removeEventListener(SCORE_EVENT, handler as EventListener);
    }, []);

    const addSeconds = useCallback((n: number, flow?: TrophyFlow) => {
        // The double-scan guard: an award triggered by a scan identical to
        // the one before it banks nothing — points, records and milestones
        // all stay untouched until a DIFFERENT scan re-arms the guard. This
        // is the single door every scoring surface walks through.
        if (currentScanIsRepeat()) return;
        addSessionSeconds(n);
        // After the bank, so the day total the tracker reads includes n.
        if (flow && Number.isFinite(n) && n > 0) recordAward(flow, Math.round(n));
    }, []);

    const reset = useCallback(() => {
        resetSessionScore();
    }, []);

    return { total, addSeconds, reset };
};
